import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  useToast,
  Badge,
  useBreakpointValue,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  InputGroup,
  InputLeftElement,
  Checkbox,
  Spinner,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Tag,
  TagLabel,
  useColorModeValue,
  Tooltip,
  SlideFade,
  useBoolean,
  Portal,
  ButtonGroup,
  Center,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCalendar,
  FiDollarSign,
  FiSearch,
  FiChevronDown,
  FiChevronRight,
  FiMoreVertical,
  FiFlag,
  FiClock,
  FiCheck,
  FiX,
  FiChevronLeft,
  FiRefreshCw,
  FiList,
  FiClock as FiTime,
  FiGrid,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { TaskService } from '../../services/taskService';
import { ProjectService } from '../../services/projectService';
import { Task, CreateTaskData, UpdateTaskData } from '../../types/task';
import { Project } from '../../types/project';
import { PageLayout } from '../../components/v0/PageLayout';
import { FloatingActionButton } from '../../components/v0/common/FloatingActionButton';
import { useAtom } from 'jotai';
import { useAtomValue, useSetAtom } from 'jotai'; // 添加 useAtomValue 和 useSetAtom
import {
  tasksAtom,
  tasksLoadingAtom,
  tasksErrorAtom,
  fetchTasksAtom,
  taskFilterDoneAtom,
  taskFilterPriorityAtom,
  taskSearchQueryAtom,
  filteredTasksAtom,
  createTaskAtom,
  updateTaskAtom,
  deleteTaskAtom
} from '../../atoms/v0/taskAtoms';
import { projectsAtom, fetchProjectsAtom } from '../../atoms/v0/projectAtoms';
import { backgroundRefreshingAtom } from '../../atoms/v0/uiAtoms';
import { v4 as uuidv4 } from 'uuid'; // Import UUID if needed for temp IDs in atom
import { TaskItem } from '../../components/v0/tasks/TaskItem';
import { ProjectGroup } from '../../components/v0/tasks/ProjectGroup';
import { TimeGroup, TimeGroupKey, getDefaultGroupOpenState } from '../../components/v0/tasks/TimeGroup';
import { TaskForm } from '../../components/v0/tasks/TaskForm';
import { IconButton as ChakraIconButton } from '@chakra-ui/react';
import { groupTasksByTime, groupTasksByProject } from '../../utils/taskUtils';
import { useTranslation } from 'react-i18next'; // 导入翻译hook
import { translatedTasksAtom, translatedProjectsAtom } from '../../atoms/v0/translationAtoms';
import { loadable } from 'jotai/utils'; // Import loadable

type ViewMode = 'project' | 'time';

// Create loadable atoms here if not defined elsewhere
const loadableProjectsAtom = loadable(projectsAtom);
const loadableTasksAtom = loadable(tasksAtom);

export const TaskList = () => {
  const maxW = useBreakpointValue({ base: '100%', md: '90%', lg: '700px' });
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const { t } = useTranslation(['v0/tasks', 'v0/common', 'common']); // 使用翻译hook，加载tasks和common命名空间

  // 获取原子和操作
  const tasks = useAtomValue(translatedTasksAtom);
  const projects = useAtomValue(translatedProjectsAtom);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // 视图模式状态
  const [viewMode, setViewMode] = useState<ViewMode>('project'); // 默认项目视图

  // 过滤器状态 - 需要读写，所以保留 useAtom
  const [filterDone, setFilterDone] = useAtom(taskFilterDoneAtom);
  const [filterPriority, setFilterPriority] = useAtom(taskFilterPriorityAtom);
  const [searchQuery, setSearchQuery] = useAtom(taskSearchQueryAtom);

  // 加载状态 (Optimistic UI might change how these are used)
  const loading = useAtomValue(tasksLoadingAtom);
  const isBackgroundRefreshing = useAtomValue(backgroundRefreshingAtom);

  // Optimistic atom actions - 使用 useSetAtom 替代 useAtom 获取写入器
  const fetchTasks = useSetAtom(fetchTasksAtom);
  const fetchProjects = useSetAtom(fetchProjectsAtom);
  const createTaskOptimistic = useSetAtom(createTaskAtom);
  const updateTaskOptimistic = useSetAtom(updateTaskAtom);
  const deleteTaskOptimistic = useSetAtom(deleteTaskAtom);

  // 项目组折叠状态
  const [openProjectIds, setOpenProjectIds] = useState<Record<string, boolean>>({});

  // 时间组折叠状态 - 默认展开今天、明天和昨天
  const [openTimeGroups, setOpenTimeGroups] = useState<Record<TimeGroupKey, boolean>>({
    noDate: false,
    future: false,
    tomorrow: true,
    today: true,
    yesterday: true,
    past: false
  });

  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSwipeTips, setShowSwipeTips] = useState(true);

  // 创建项目ID到项目对象的映射，用于TimeGroup中显示项目名称
  const projectsLoadable = useAtomValue(loadableProjectsAtom);
  const translatedProjects = useAtomValue(translatedProjectsAtom);
  const tasksLoadable = useAtomValue(loadableTasksAtom);
  const translatedTasks = useAtomValue(translatedTasksAtom);

  const projectsMap = useMemo(() => {
    const map: Record<string, Project> = {};
    (translatedProjects ?? (projectsLoadable.state === 'hasData' ? projectsLoadable.data : [])).forEach(p => {
      map[p.id] = p;
    });
    return map;
  }, [translatedProjects, projectsLoadable]);

  // We don't need tasksWithProjects anymore since we're using filteredTasksAtom directly

  // ... useEffect for tips and initial fetch ...
  useEffect(() => {
    const dismissed = localStorage.getItem('swipeTipsDismissed') === 'true';
    setShowSwipeTips(!dismissed);
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchProjects();
  }, [fetchTasks, fetchProjects]);

  // 读取上次保存的视图模式
  useEffect(() => {
    const savedViewMode = localStorage.getItem('taskViewMode');
    if (savedViewMode === 'project' || savedViewMode === 'time') {
      setViewMode(savedViewMode);
    }
  }, []);

  // 保存当前视图模式
  useEffect(() => {
    localStorage.setItem('taskViewMode', viewMode);
  }, [viewMode]);

  // 获取过滤后的任务
  const filteredTasks = useAtomValue(filteredTasksAtom);

  // 按项目分组任务 - 使用过滤后的任务
  const tasksByProject = groupTasksByProject(filteredTasks);

  // 按时间分组任务 - 使用过滤后的任务
  const tasksByTime = groupTasksByTime(filteredTasks);

  // 切换项目分组的折叠状态
  const toggleProject = (projectId: string) => {
    setOpenProjectIds(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  // 切换时间分组的折叠状态
  const toggleTimeGroup = (groupKey: TimeGroupKey) => {
    setOpenTimeGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // 切换视图模式
  const toggleViewMode = () => {
    setViewMode(prev => (prev === 'project' ? 'time' : 'project'));
  };

  // 视图切换按钮组件
  const ViewModeToggleButton = () => (
    <Button
      leftIcon={viewMode === 'project' ? <FiTime size={18} /> : <FiList size={18} />}
      onClick={toggleViewMode}
      colorScheme="pink"
      variant="ghost"
      size="sm"
      fontWeight="normal"
      fontSize="sm"
      px={3}
      py={1.5}
      h="auto"
      borderRadius="full"
      _hover={{ bg: 'pink.50', color: 'pink.600' }}
      _dark={{ _hover: { bg: 'pink.900', color: 'pink.200' } }}
    >
      {viewMode === 'project' ? t('v0/tasks:viewMode.time') : t('v0/tasks:viewMode.project')}
    </Button>
  );

  // Optimistic Handlers - 保持不变
  const handleToggleDone = async (task: Task, done: boolean) => {
    const partialUpdateData: UpdateTaskData = { id: task.id, done };
    const success = await updateTaskOptimistic(partialUpdateData);
    if (!success) { toast({ title: t('v0/tasks:notifications.error'), description: t('v0/tasks:notifications.errorDescription', '请稍后重试或检查网络连接。'), status: 'error', duration: 3000, isClosable: true, position: 'top' }); }
  };

  const handleQuickAddTask = async (projectId: string | null, title: string, startAt?: string) => {
    const newTaskData: CreateTaskData = {
      title: title.trim(),
      notes: '',
      done: false,
      vital: false,
      urgent: false,
      fixed: false,
      projectId: projectId || undefined,
      startAt: startAt
    };

    if (!newTaskData.title) {
      toast({ title: t('v0/tasks:form.titleRequired'), status: 'warning', duration: 2000, isClosable: true, position: 'top' });
      return;
    }

    const success = await createTaskOptimistic(newTaskData);
    if (!success) {
      toast({ title: t('v0/tasks:notifications.error'), description: t('v0/tasks:notifications.quickAddError', '未能添加任务，请重试。'), status: 'error', duration: 3000, isClosable: true, position: 'top' });
    }
  };

  const handleDeleteTaskOptimistic = async (id: string) => {
    if (!id) { toast({ title: t('v0/tasks:notifications.error'), description: t('v0/tasks:notifications.deleteInvalidIdError', '任务ID无效。'), status: 'error', duration: 3000, isClosable: true, position: 'top' }); return; }
    const success = await deleteTaskOptimistic(id);
    if (!success) { toast({ title: t('v0/tasks:notifications.error'), description: t('v0/tasks:notifications.deleteError', '未能删除任务，请重试。'), status: 'error', duration: 3000, isClosable: true, position: 'top' }); }
  };

  const handleFormSubmit = async (data: CreateTaskData | UpdateTaskData) => {
    if (!data.title?.trim()) { toast({ title: t('v0/tasks:form.titleRequired'), status: 'warning', duration: 2000, isClosable: true, position: 'top'}); return; }
    setIsSubmitting(true); let success = false;
    try {
      if ('id' in data && data.id) { success = await updateTaskOptimistic(data as UpdateTaskData); }
      else { success = await createTaskOptimistic(data as CreateTaskData); }
      if (success) { toast({ title: ('id' in data && data.id) ? t('v0/tasks:notifications.updateSuccess') : t('v0/tasks:notifications.createSuccess'), status: 'success', duration: 3000, isClosable: true, position: 'top' }); onClose(); if ('id' in data && data.id) setSelectedTask(null); }
      // else { /* Optional error toast, atom might handle */ }
    } finally { setIsSubmitting(false); }
  };

  const handleCancel = () => { console.log("TaskList: handleCancel called, attempting to onClose..."); onClose(); };
  const handleSelectTask = (task: Task) => { console.log('TaskList: handleSelectTask called, calling onOpen for task:', task.id); setSelectedTask(task); onOpen(); };
  const openCreateTaskForm = () => { setSelectedTask(null); onOpen(); };
  const handleSearch = () => { setFilterDone(null); setFilterPriority(null); };
  const resetFilters = () => { setSearchQuery(''); setFilterDone(null); setFilterPriority(null); };
  const dismissTips = () => { setShowSwipeTips(false); localStorage.setItem('swipeTipsDismissed', 'true'); };

  // 渲染项目视图
  const renderProjectView = () => {
    // 检查是否有过滤条件
    const hasFilters = filterDone !== null || filterPriority !== null || searchQuery !== '';

    // 对项目进行排序
    let sortedProjects = [...projects];

    // 默认项目始终排在最前面（无论是否有过滤条件）
    sortedProjects.sort((a, b) => {
      if (a.title === '默认项目') return -1;
      if (b.title === '默认项目') return 1;
      return 0;
    });

    // 如果有过滤条件，再按是否有任务排序（有任务的在前，无任务的在后）
    if (hasFilters) {
      sortedProjects.sort((a, b) => {
        // 保持默认项目在最前面
        if (a.title === '默认项目') return -1;
        if (b.title === '默认项目') return 1;

        const tasksA = tasksByProject[a.id] || [];
        const tasksB = tasksByProject[b.id] || [];

        // 只按是否有任务排序（有任务的在前），不再按任务数量排序
        if (tasksA.length === 0 && tasksB.length > 0) return 1;
        if (tasksA.length > 0 && tasksB.length === 0) return -1;

        // 对于都有任务或都没有任务的项目，保持原始顺序
        return 0;
      });
    }

    return (
      <VStack spacing={0} align="stretch">
        {/* 无分组任务 - 特殊情况，只有非空时才显示 */}
        {tasksByProject['none'] && tasksByProject['none'].length > 0 && (
          <ProjectGroup
            project={null}
            tasks={tasksByProject['none']}
            isOpen={openProjectIds['none'] !== false}
            onToggle={() => toggleProject('none')}
            onSelectTask={handleSelectTask}
            onToggleDone={handleToggleDone}
            onDeleteTask={handleDeleteTaskOptimistic}
            onQuickAddTask={handleQuickAddTask}
          />
        )}
        {/* 项目任务 - 根据排序后的项目列表渲染 */}
        {sortedProjects.map(project => {
          const projectTasks = tasksByProject[project.id] || [];

          return (
            <ProjectGroup
              key={project.id}
              project={project}
              tasks={projectTasks}
              isOpen={openProjectIds[project.id] !== false}
              onToggle={() => toggleProject(project.id)}
              onSelectTask={handleSelectTask}
              onToggleDone={handleToggleDone}
              onDeleteTask={handleDeleteTaskOptimistic}
              onQuickAddTask={handleQuickAddTask}
            />
          );
        })}
      </VStack>
    );
  };

  // 渲染时间视图
  const renderTimeView = () => {
    // 检查是否有过滤条件
    const hasFilters = filterDone !== null || filterPriority !== null || searchQuery !== '';

    // 默认时间组顺序
    let timeGroups: TimeGroupKey[] = ['today', 'tomorrow', 'yesterday', 'future', 'past', 'noDate'];

    // 如果有过滤条件，对时间组进行排序：有任务的在前，无任务的在后
    if (hasFilters) {
      timeGroups.sort((a, b) => {
        const tasksA = tasksByTime[a] || [];
        const tasksB = tasksByTime[b] || [];
        // 先按是否有任务排序（有任务的在前），再按任务数量排序（多的在前）
        if (tasksA.length === 0 && tasksB.length > 0) return 1;
        if (tasksA.length > 0 && tasksB.length === 0) return -1;

        // 如果两组都有任务，保持原有顺序
        const originalOrderA = ['today', 'tomorrow', 'yesterday', 'future', 'past', 'noDate'].indexOf(a);
        const originalOrderB = ['today', 'tomorrow', 'yesterday', 'future', 'past', 'noDate'].indexOf(b);
        return originalOrderA - originalOrderB;
      });
    }

    return (
      <VStack spacing={0} align="stretch">
        {/* 根据排序后的时间组列表渲染 */}
        {timeGroups.map((timeKey) => {
          const groupKey = timeKey as TimeGroupKey;
          const groupTasks = tasksByTime[groupKey] || [];

          return (
            <TimeGroup
              key={groupKey}
              groupKey={groupKey}
              tasks={groupTasks}
              isOpen={openTimeGroups[groupKey]}
              onToggle={() => toggleTimeGroup(groupKey)}
              onSelectTask={handleSelectTask}
              onToggleDone={handleToggleDone}
              onDeleteTask={handleDeleteTaskOptimistic}
              onQuickAddTask={handleQuickAddTask}
              projectsMap={projectsMap}
            />
          );
        })}
      </VStack>
    );
  };

  console.log('TaskList: Rendering, isOpen state is:', isOpen);

  // Ensure data is loaded before proceeding
  if (tasksLoadable.state !== 'hasData' || projectsLoadable.state !== 'hasData') {
    return null; // Or return loading/error state again
  }

  return (
    <PageLayout
      title={t('v0/tasks:title')}
      customAction={<ViewModeToggleButton />}
    >
      <Container maxW={maxW} py={4} px={{ base: 2, md: 4 }}>
        {/* 搜索栏和过滤器 */}
        <Flex mb={4} justify="space-between" direction={{ base: 'row', md: 'row' }} gap={2}>
          <Flex flex="1" maxW={{ base: '100%', md: '60%' }}>
            <InputGroup size="md">
              <InputLeftElement>
                <Box as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder={t('v0/tasks:filters.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                borderRadius="md"
              />
            </InputGroup>
          </Flex>

          <HStack spacing={2}>
            <Menu closeOnSelect={false}>
              <MenuButton
                as={IconButton}
                aria-label={t('v0/common:actions.filter')}
                icon={<FiChevronDown />}
                variant="ghost"
                colorScheme="pink"
              />
              <Portal>
                <MenuList minW="180px">
                  <MenuItem closeOnSelect={false} fontWeight={filterDone === true ? "bold" : "normal"} onClick={() => setFilterDone(filterDone === true ? null : true)}>
                    {filterDone === true && <Box as={FiCheck} mr={2} />} {t('v0/tasks:filters.showCompleted')}
                  </MenuItem>
                  <MenuItem closeOnSelect={false} fontWeight={filterDone === false ? "bold" : "normal"} onClick={() => setFilterDone(filterDone === false ? null : false)}>
                    {filterDone === false && <Box as={FiCheck} mr={2} />} {t('v0/tasks:filters.showUncompleted')}
                  </MenuItem>
                  <MenuItem closeOnSelect={false} fontWeight={filterPriority === 'vital' ? "bold" : "normal"} onClick={() => setFilterPriority(filterPriority === 'vital' ? null : 'vital')}>
                    {filterPriority === 'vital' && <Box as={FiCheck} mr={2} />} {t('v0/tasks:filters.showImportant')}
                  </MenuItem>
                  {(filterDone !== null || filterPriority !== null || searchQuery) && (
                    <MenuItem icon={<FiX />} onClick={resetFilters}>{t('v0/tasks:filters.clearAll')}</MenuItem>
                  )}
                </MenuList>
              </Portal>
            </Menu>
          </HStack>
        </Flex>

        {/* 任务列表内容 */}
        {loading && !isBackgroundRefreshing ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="xl" color="pink.500" />
          </Flex>
        ) : tasks.length === 0 && projects.length === 0 ? (
          <Box
            textAlign="center"
            py={10}
            bg={useColorModeValue('white', 'gray.800')}
            borderRadius="lg"
            boxShadow="sm"
            mt={4}
            borderWidth="1px"
            borderColor={useColorModeValue('gray.200', 'gray.700')}
          >
            <Text color={useColorModeValue('gray.400', 'gray.500')} fontSize="5xl" mb={2}>{t('common:icons.notepad')}</Text>
            <Heading size="md" mb={2} color={useColorModeValue('gray.700', 'gray.200')}>{t('v0/tasks:emptyState.title')}</Heading>
            <Text color={useColorModeValue('gray.500', 'gray.400')} mb={6} px={4}>
              {t('v0/tasks:emptyState.description')}
            </Text>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="pink"
              onClick={openCreateTaskForm}
              borderRadius="full"
              px={6}
              boxShadow="md"
              _hover={{ transform: "scale(1.05)" }}
            >
              {t('v0/tasks:emptyState.action')}
            </Button>
          </Box>
        ) : (
          <>
            {/* 视图切换说明 */}
            {viewMode === 'time' && (
              <Center mb={4}>
                <Text fontSize="sm" color="gray.500">
                  {t('v0/tasks:timeView.explanation')}
                </Text>
              </Center>
            )}

            {/* 渲染当前视图 */}
            {viewMode === 'project' ? renderProjectView() : renderTimeView()}

            {/* 提示 */}
            {!isDesktop && showSwipeTips && (
              <SlideFade in={true} offsetY="20px">
                <Flex
                  bg={useColorModeValue('pink.50', 'pink.900')}
                  p={3}
                  borderRadius="md"
                  mt={4}
                  alignItems="center"
                  justifyContent="space-between"
                  boxShadow="sm"
                  borderWidth="1px"
                  borderColor={useColorModeValue('pink.100', 'pink.700')}
                >
                  <Text fontSize="sm" color={useColorModeValue('pink.700', 'pink.200')}>
                    {t('v0/tasks:actions.swipeTip', '向左滑动快速操作，右滑展开详情')}
                  </Text>
                  <IconButton
                    aria-label={t('v0/common:actions.close')}
                    icon={<FiX />}
                    size="xs"
                    variant="ghost"
                    colorScheme="pink"
                    onClick={dismissTips}
                  />
                </Flex>
              </SlideFade>
            )}
          </>
        )}

        {/* 添加任务浮动按钮 */}
        <FloatingActionButton
          onClick={openCreateTaskForm}
          colorScheme="pink"
          icon={<FiPlus />}
          position={{
            bottom: {
              base: "100px", // 移动端增加高度
              md: "120px",   // 平板和桌面增加更多高度，确保不被底部导航遮挡
              lg: "120px"    // 大屏幕保持同样的高度
            },
            right: { base: "20px", md: "40px" }
          }}
          showOnDesktop={true}
        />

        {/* 任务编辑模态框 */}
        {isDesktop ? (
          <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{selectedTask ? t('v0/tasks:form.editTitle') : t('v0/tasks:form.createTitle')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <TaskForm
                  task={selectedTask}
                  onSubmit={handleFormSubmit}
                  onCancel={handleCancel}
                  isSubmitting={isSubmitting}
                />
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : (
          <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} size="full">
            <DrawerOverlay />
            <DrawerContent borderTopRadius="xl">
              <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px">{selectedTask ? t('v0/tasks:form.editTitle') : t('v0/tasks:form.createTitle')}</DrawerHeader>
              <DrawerBody py={4}>
                <TaskForm
                  task={selectedTask}
                  onSubmit={handleFormSubmit}
                  onCancel={handleCancel}
                  isSubmitting={isSubmitting}
                />
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        )}
      </Container>
    </PageLayout>
  );
};